import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const SECTIONS = [
  {
    title: '1. Introduction',
    body: [
      'PHYSIGN Technologies Pvt. Ltd. (“PHYSIGN”, “we”, “our” or “us”) is a healthcare technology platform that helps users discover and book healthcare professionals for clinic consultations and home healthcare services.',
      'We respect your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, share and protect information when you visit our website, create an account, book an appointment or use our services.',
      'By using PHYSIGN, you acknowledge that you have read this Privacy Policy. Where consent is legally required, we will request it separately.',
    ],
  },
  {
    title: '2. Scope of This Policy',
    body: ['This Privacy Policy applies to:'],
    list: [
      'Patients and individuals searching for healthcare services.',
      'Doctors and medical practitioners.',
      'Nurses and physiotherapists.',
      'Clinics and other healthcare providers.',
      'Visitors to our website and users of our digital services.',
    ],
  },
  {
    title: '3. Information We Collect',
    body: ['Depending on how you use PHYSIGN, we may collect the following categories of information.'],
    subsections: [
      {
        title: 'A. Personal Information',
        list: [
          'Full name.',
          'Mobile number and email address.',
          'Account login and authentication information.',
          'Age or date of birth, where necessary.',
          'City, address and service location.',
          'Profile photograph and account preferences.',
        ],
      },
      {
        title: 'B. Healthcare Information',
        body: ['When necessary for providing our services, we may collect:'],
        list: [
          'Appointment details and consultation preferences.',
          'Information about the type of healthcare service requested.',
          'Medical history or symptoms voluntarily provided for a booking.',
          'Information required to coordinate a healthcare visit.',
          'Relevant information submitted by healthcare professionals.',
        ],
        footer: 'We treat health-related information with appropriate confidentiality and safeguards and seek consent where required by applicable law.',
      },
      {
        title: 'C. Healthcare Provider Information',
        body: ['For doctor, nurse, physiotherapist and clinic accounts, we may collect:'],
        list: [
          'Professional name and contact details.',
          'Qualifications and professional registration details.',
          'Relevant certificates and identity documents.',
          'Clinic address, specialty and service areas.',
          'Availability, consultation fees and service information.',
          'Bank or payment details where necessary for settlements.',
        ],
        footer: 'We use relevant professional information to support provider verification, profile management and service delivery.',
      },
      {
        title: 'D. Technical Information',
        body: [
          'When you access our website, we may collect certain technical information, including IP address, browser type, device information, access logs, cookies and website usage data, depending on the technologies enabled.',
        ],
      },
    ],
  },
  {
    title: '4. How We Use Your Information',
    body: ['PHYSIGN may use personal information to:'],
    ordered: true,
    list: [
      'Create and manage user accounts.',
      'Help patients discover healthcare professionals.',
      'Facilitate clinic consultation and home-visit bookings.',
      'Verify healthcare provider credentials.',
      'Coordinate appointments and communicate booking updates.',
      'Process payments, refunds and provider settlements where supported.',
      'Respond to questions, complaints and support requests.',
      'Maintain platform security and prevent fraud or misuse.',
      'Improve website performance and user experience.',
      'Meet legal, regulatory and record-keeping obligations.',
      'Send promotional communications where permitted and, where required, with your consent.',
    ],
    footer: 'We do not use personal information for purposes incompatible with the purposes described above without an appropriate legal basis or consent where required.',
  },
  {
    title: '5. Healthcare Provider Verification',
    body: [
      'PHYSIGN may review relevant professional registration details, qualifications and documents to verify healthcare providers.',
      'Verification is intended to support trust and platform integrity. It does not constitute a guarantee of clinical outcomes, professional performance or the quality of every service.',
      'We may restrict, suspend or remove a provider profile if verification requirements are not met or if there are credible concerns about the accuracy of the information provided.',
    ],
  },
  {
    title: '6. Appointment and Home-Visit Information',
    body: ['To facilitate a booking, PHYSIGN may share relevant information between the patient and the selected healthcare provider, such as:'],
    list: [
      'Name and necessary contact information.',
      'Appointment date and time.',
      'Service type and visit location.',
      'Information relevant to arranging the requested service.',
    ],
    footer: 'We aim to limit information sharing to what is reasonably necessary for the booking and service. Users should avoid sharing unnecessary medical details or sensitive personal information through general communication channels.',
  },
  {
    title: '7. Sharing of Information',
    body: [
      'PHYSIGN does not sell personal information to third parties as a business practice.',
      'We may share relevant information with:',
    ],
    list: [
      ['Healthcare providers:', 'To facilitate requested bookings and services.'],
      ['Payment service providers:', 'To process transactions, refunds and settlements.'],
      ['Technology and hosting providers:', 'To operate our website, database, authentication, storage and communications.'],
      ['Customer support providers:', 'To address service requests and complaints.'],
      ['Professional advisers:', 'Where necessary for legal, accounting or compliance purposes.'],
      ['Government or regulatory authorities:', 'When disclosure is legally required or otherwise permitted by applicable law.'],
      ['Business successors:', 'In connection with a merger, restructuring, acquisition or transfer of business, subject to applicable legal requirements.'],
    ],
    footer: 'Third-party services may process information under their own privacy policies and contractual obligations. We take reasonable steps to work with service providers that apply appropriate safeguards.',
  },
  {
    title: '8. Cookies and Analytics',
    body: ['PHYSIGN may use cookies and similar technologies to:'],
    list: [
      'Maintain sessions and account functionality.',
      'Remember user preferences.',
      'Understand website usage.',
      'Improve platform performance and security.',
    ],
    footer: 'You may be able to manage cookies through your browser settings. Disabling certain cookies may affect website functionality. Where required by applicable law, we will seek consent before using non-essential cookies or similar tracking technologies.',
  },
  {
    title: '9. Data Storage, Security and Retention',
    body: [
      'We use reasonable technical, organisational and administrative measures to protect personal information against unauthorised access, alteration, disclosure, loss or misuse.',
      'Depending on the information involved, safeguards may include access controls, authentication, restricted permissions, secure transmission and appropriate data backup practices.',
      'No electronic system can be guaranteed to be completely secure.',
      'We retain personal information only for as long as reasonably necessary for the purposes described in this Policy, including service delivery, dispute resolution, legal compliance and legitimate record-keeping requirements. Information may then be deleted, anonymised or securely disposed of, subject to applicable law.',
    ],
  },
  {
    title: '10. Your Privacy Rights and Choices',
    body: ['Subject to applicable law, you may be able to:'],
    list: [
      'Request access to your personal information.',
      'Request correction of inaccurate or incomplete information.',
      'Withdraw consent where processing is based on consent.',
      'Request deletion of personal information, where legally permissible.',
      'Opt out of promotional communications.',
      'Raise a privacy complaint or request assistance.',
    ],
    footer: 'Some information may need to be retained where required by law or necessary to resolve disputes, protect security or maintain essential records. To exercise a privacy right, email info@physign.com with the subject line “Privacy Request”. We may need to verify your identity before processing the request.',
  },
  {
    title: '11. Children’s Privacy',
    body: [
      'PHYSIGN is not intended to permit children to independently create accounts or submit personal information where parental or guardian consent is legally required.',
      'Where a parent or lawful guardian uses PHYSIGN to arrange healthcare services for a child, we may process the information necessary for that purpose in accordance with applicable law.',
      'If we become aware that personal information has been collected unlawfully from a child, we will take appropriate steps to address it.',
    ],
  },
  {
    title: '12. Third-Party Links and Services',
    body: [
      'Our website may contain links to third-party websites, payment services or communication platforms. PHYSIGN does not control the privacy practices of independent third parties.',
      'We encourage users to review the privacy policies of those services before sharing personal information with them.',
    ],
  },
  {
    title: '13. Data Transfers',
    body: [
      'Our technology providers may process or store information in locations outside the city or state where you reside. Where information is transferred across national borders, PHYSIGN will take steps required by applicable law and relevant contractual safeguards.',
    ],
  },
  {
    title: '14. Marketing Communications',
    body: [
      'PHYSIGN may send service-related messages, such as appointment confirmations, reminders, verification updates and important account notices.',
      'Promotional messages will be sent in accordance with applicable requirements. You may opt out of promotional communications using the available unsubscribe option or by contacting us. Opting out of marketing does not necessarily stop essential service communications.',
    ],
  },
  {
    title: '15. Applicable Laws',
    body: [
      'PHYSIGN aims to handle personal information in accordance with applicable Indian privacy and data protection laws, including the Information Technology Act, 2000, relevant rules and the Digital Personal Data Protection Act, 2023, to the extent applicable and in force.',
      'Our practices may be updated as legal requirements and the platform’s services evolve.',
    ],
  },
  {
    title: '16. Changes to This Privacy Policy',
    body: [
      'We may update this Privacy Policy to reflect changes in our services, technology, business operations or legal obligations.',
      'The revised version will be published on our website with an updated “Last Updated” date. Where required by law, we will provide notice or seek consent for material changes.',
    ],
  },
];

const ListItem = ({ item }) => (
  <li className="leading-relaxed">
    {Array.isArray(item) ? (<><strong className="text-slate-800">{item[0]}</strong> {item[1]}</>) : item}
  </li>
);

const Content = ({ data }) => (
  <>
    {data.body && data.body.map((p, i) => (
      <p key={i} className="text-slate-600 leading-relaxed mb-2">{p}</p>
    ))}
    {data.list && !data.ordered && (
      <ul className="list-disc pl-6 space-y-1.5 text-slate-600">
        {data.list.map((item, i) => <ListItem key={i} item={item} />)}
      </ul>
    )}
    {data.list && data.ordered && (
      <ol className="list-decimal pl-6 space-y-1.5 text-slate-600">
        {data.list.map((item, i) => <ListItem key={i} item={item} />)}
      </ol>
    )}
  </>
);

const Block = ({ data }) => (
  <section>
    <h2 className="text-lg md:text-xl font-semibold text-slate-900 mb-3">{data.title}</h2>
    <Content data={data} />
    {data.subsections && (
      <div className="space-y-6 mt-4">
        {data.subsections.map((sub) => (
          <div key={sub.title}>
            <h3 className="text-base md:text-lg font-semibold text-slate-800 mb-2">{sub.title}</h3>
            <Content data={sub} />
            {sub.footer && <p className="text-slate-600 leading-relaxed mt-3">{sub.footer}</p>}
          </div>
        ))}
      </div>
    )}
    {data.footer && <p className="text-slate-600 leading-relaxed mt-3">{data.footer}</p>}
  </section>
);

const PrivacyPolicy = () => (
  <div className="min-h-screen bg-white" data-testid="privacy-page">
    <SEO
      path="/privacy-policy"
      title="Privacy Policy"
      description="Read the Privacy Policy of Physign Technologies Pvt. Ltd. to understand how we collect, use, share and protect your personal and healthcare information."
    />

    <header className="border-b border-slate-100 bg-white sticky top-0 z-30">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="privacy-logo-link">
          <span className="text-xl font-bold text-[#3B1C73]" style={{ fontFamily: 'Plus Jakarta Sans' }}>
            Physign
          </span>
        </Link>
        <nav className="flex items-center gap-6 text-sm">
          <Link to="/" className="text-slate-600 hover:text-[#3B1C73] hidden sm:inline">Home</Link>
          <Link to="/about" className="text-slate-600 hover:text-[#3B1C73] hidden sm:inline">About</Link>
          <Link to="/auth" className="bg-[#3B1C73] text-white px-4 py-2 rounded-full font-medium hover:bg-[#2c1457]" data-testid="privacy-login-link">
            Login / Sign Up
          </Link>
        </nav>
      </div>
    </header>

    <main className="max-w-4xl mx-auto px-6 py-12 md:py-16">
      <div className="mb-10 pb-8 border-b border-slate-200">
        <span className="text-[#3B1C73] font-semibold uppercase tracking-wide text-sm">Legal</span>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mt-3" data-testid="privacy-last-updated">Last updated: 15 October 2026</p>
        <p className="text-slate-600 mt-4 leading-relaxed">
          This Privacy Policy explains how PHYSIGN Technologies Pvt. Ltd. collects, uses, stores, shares and
          protects your information when you use our website and services.
        </p>
      </div>

      <div className="space-y-8">
        {SECTIONS.map((section) => (
          <Block key={section.title} data={section} />
        ))}

        <section className="bg-purple-50 border border-purple-100 rounded-2xl p-6 mt-12" data-testid="privacy-contact">
          <h2 className="text-lg md:text-xl font-semibold text-[#3B1C73] mb-3">17. Contact Us</h2>
          <p className="text-slate-700 leading-relaxed mb-3">
            For questions, privacy requests, complaints or concerns regarding this Privacy Policy, contact:
          </p>
          <p className="text-slate-700 leading-relaxed">
            <strong>PHYSIGN Technologies Pvt. Ltd.</strong>
            <br />
            <strong>Registered Office:</strong> HADA Complex, Wazirabad, Sector 52, Near Apna Dhaba, Gurugram, Haryana – 122003, India.
            <br />
            Email:{' '}
            <a href="mailto:info@physign.com" className="text-[#3B1C73] underline hover:no-underline">info@physign.com</a>
            <br />
            Website:{' '}
            <a href="https://physign.com" className="text-[#3B1C73] underline hover:no-underline">https://physign.com</a>
            <br />
            Subject Line: Privacy Policy / Data Protection Request
          </p>
          <p className="text-slate-600 leading-relaxed mt-3">
            We will review privacy-related requests and respond in accordance with applicable legal requirements.
          </p>
        </section>
      </div>
    </main>

    <footer className="border-t border-slate-100 py-6 mt-10">
      <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-slate-500">
        <p>© 2026 Physign Technologies Pvt. Ltd. All rights reserved.</p>
        <div className="flex gap-5">
          <Link to="/about" className="hover:text-[#3B1C73]">About</Link>
          <Link to="/terms" className="hover:text-[#3B1C73]">Terms</Link>
          <Link to="/privacy-policy" className="hover:text-[#3B1C73]">Privacy</Link>
        </div>
      </div>
    </footer>
  </div>
);

export default PrivacyPolicy;
